import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FileText, Table2, Plus, RefreshCcw, Trash2, Download, Wand2 } from "lucide-react";

const API_URL = "https://apex-cgs-backend.onrender.com";

const DOC_TYPES = [
  "BIA",
  "BCMS_GAP",
  "RISK",
  "Products",
  "Recovery Performance",
  "Activity Recovery",
];

export default function App() {
  const [activeTab, setActiveTab] = useState("master");

  // selected dataset
  const [docType, setDocType] = useState("BIA");

  // dataset state: { doc_type, columns, rows }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dataset, setDataset] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // documents state
  const [docLoading, setDocLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [docError, setDocError] = useState("");

  const api = axios.create({ baseURL: API_URL });

  const columns = useMemo(() => dataset?.columns ?? [], [dataset]);
  const rows = useMemo(() => dataset?.rows ?? [], [dataset]);

  async function loadDataset(type = docType) {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const res = await api.get(`/datasets/${encodeURIComponent(type)}`);
      setDataset(res.data);
    } catch (e) {
      console.error(e);
      setError(`Failed to load ${type} dataset. Check API_URL and backend status.`);
    } finally {
      setLoading(false);
    }
  }

  async function initDataset() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await api.post(`/datasets/${encodeURIComponent(docType)}/init`);
      setDataset(res.data);
      setMessage(`Initialized empty ${docType} master table.`);
    } catch (e) {
      console.error(e);
      setError(`Failed to initialize ${docType}.`);
    } finally {
      setBusy(false);
    }
  }

  async function saveDataset() {
    if (!dataset) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await api.put(`/datasets/${encodeURIComponent(docType)}`, dataset);
      setDataset(res.data);
      setMessage(`Saved ${docType} master table.`);
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detail
          ? `Save failed: ${JSON.stringify(e.response.data.detail)}`
          : "Save failed (check console).";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function generateDoc() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await api.post(`/generate/${encodeURIComponent(docType)}`);
      setMessage(`Generated: ${res.data.filename}`);
      await loadDocuments();
      window.open(`${API_URL}/documents/${encodeURIComponent(res.data.filename)}`, "_blank");
    } catch (e) {
      console.error(e);
      setError(`Failed to generate ${docType}. Check template/placeholder names.`);
    } finally {
      setBusy(false);
    }
  }

  function addRow() {
    if (!dataset) return;
    const empty = {};
    for (const c of dataset.columns) empty[c] = "";
    setDataset((prev) => ({ ...prev, rows: [...prev.rows, empty] }));
  }

  function deleteRow(idx) {
    if (!dataset) return;
    const next = [...dataset.rows];
    next.splice(idx, 1);
    setDataset((prev) => ({ ...prev, rows: next }));
  }

  function updateCell(rowIndex, col, value) {
    setDataset((prev) => {
      const next = structuredClone(prev);
      next.rows[rowIndex][col] = value;
      return next;
    });
  }

  async function loadDocuments() {
    setDocLoading(true);
    setDocError("");
    try {
      const res = await api.get("/documents");
      setDocuments(res.data.documents || []);
    } catch (e) {
      console.error(e);
      setDocError("Failed to load documents.");
    } finally {
      setDocLoading(false);
    }
  }

  function downloadDocument(filename) {
    window.open(`${API_URL}/documents/${encodeURIComponent(filename)}`, "_blank");
  }

  async function deleteDocument(filename) {
    if (!window.confirm(`Delete ${filename}?`)) return;
    try {
      await api.delete(`/documents/${encodeURIComponent(filename)}`);
      await loadDocuments();
    } catch (e) {
      console.error(e);
      window.alert("Failed to delete document.");
    }
  }

  // initial load
  useEffect(() => {
    loadDataset("BIA");
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when dropdown changes, load that dataset
  useEffect(() => {
    loadDataset(docType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur border-b border-slate-700/60">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
              <Wand2 className="text-cyan-300" size={20} />
            </div>
            <div>
              <div className="text-lg font-bold">APEX CGS WEB</div>
              <div className="text-xs text-slate-400">
                Master tables → derived tables → Word template → DOCX
              </div>
            </div>
          </div>

          <button
            onClick={async () => {
              await loadDataset(docType);
              await loadDocuments();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700"
          >
            <RefreshCcw size={18} />
            Refresh
          </button>
        </div>

        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 overflow-x-auto">
            <TabButton
              active={activeTab === "master"}
              onClick={() => setActiveTab("master")}
              icon={<Table2 size={18} />}
              label="Master Tables"
            />
            <TabButton
              active={activeTab === "documents"}
              onClick={() => setActiveTab("documents")}
              icon={<FileText size={18} />}
              label="Documents"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === "master" && (
          <Card>
            <CardHeader
              title="Master Tables"
              subtitle="Pick a master dataset, edit it, save, then generate the matching DOCX."
              right={
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {/* dropdown */}
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-slate-100"
                    disabled={busy}
                    title="Select which master dataset to edit"
                  >
                    {DOC_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>

                  <button
                    disabled={busy}
                    onClick={initDataset}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 disabled:opacity-60"
                  >
                    <Plus size={16} /> Initialize Empty
                  </button>

                  <button
                    disabled={busy || !dataset}
                    onClick={addRow}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 disabled:opacity-60"
                  >
                    <Plus size={16} /> Add Row
                  </button>

                  <button
                    disabled={busy || !dataset}
                    onClick={saveDataset}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-60"
                  >
                    Save
                  </button>

                  <button
                    disabled={busy || !dataset}
                    onClick={generateDoc}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-60"
                  >
                    <Wand2 size={16} /> Generate {docType} DOCX
                  </button>
                </div>
              }
            />

            {loading ? (
              <div className="text-slate-400">Loading {docType}…</div>
            ) : (
              <>
                {error && <Notice type="error">{error}</Notice>}
                {message && <Notice>{message}</Notice>}

                {!dataset ? (
                  <div className="text-slate-400">No dataset loaded.</div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-xs text-slate-400">
                      Backend: <span className="font-mono text-slate-200">{API_URL}</span>
                    </div>

                    {/* Bigger, readable grid */}
                    <div className="overflow-x-auto border border-slate-700/60 rounded-2xl">
                      <table className="w-full">
                        <thead className="bg-slate-800/60">
                          <tr>
                            <Th className="w-[120px]">Actions</Th>
                            {columns.map((c) => (
                              <Th key={c} className="min-w-[220px]">
                                {c}
                              </Th>
                            ))}
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-700/60">
                          {rows.length === 0 ? (
                            <tr>
                              <Td className="text-slate-400" colSpan={columns.length + 1}>
                                No rows yet. Click “Add Row”.
                              </Td>
                            </tr>
                          ) : (
                            rows.map((r, rowIndex) => (
                              <tr key={rowIndex} className="hover:bg-slate-800/20 align-top">
                                <Td>
                                  <button
                                    disabled={busy}
                                    onClick={() => deleteRow(rowIndex)}
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 disabled:opacity-60"
                                  >
                                    <Trash2 size={16} /> Delete
                                  </button>
                                </Td>

                                {columns.map((c) => {
                                  const val = r?.[c] ?? "";
                                  return (
                                    <Td key={c}>
                                      <textarea
                                        value={val}
                                        onChange={(e) => updateCell(rowIndex, c, e.target.value)}
                                        placeholder={c}
                                        title={val} // hover shows full text
                                        className="w-full min-w-[220px] px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white outline-none focus:border-cyan-500/60"
                                        style={{
                                          minHeight: 44,
                                          resize: "vertical",
                                          lineHeight: "1.25rem",
                                        }}
                                        onFocus={(e) => {
                                          // expand a bit on focus so long text is readable
                                          e.currentTarget.style.minHeight = "88px";
                                        }}
                                        onBlur={(e) => {
                                          e.currentTarget.style.minHeight = "44px";
                                        }}
                                      />
                                    </Td>
                                  );
                                })}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="text-xs text-slate-400">
                      Tip: Click <span className="text-slate-200 font-semibold">Save</span> before generating so the DB has the latest rows.
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        )}

        {activeTab === "documents" && (
          <Card>
            <CardHeader
              title="Generated Documents"
              subtitle="List, download, and delete generated DOCX files."
              right={
                <button
                  onClick={loadDocuments}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700"
                >
                  <RefreshCcw size={16} /> Reload
                </button>
              }
            />

            {docError && <Notice type="error">{docError}</Notice>}

            {docLoading ? (
              <div className="text-slate-400">Loading…</div>
            ) : documents.length === 0 ? (
              <div className="text-slate-400">No documents yet. Generate one from Master Tables.</div>
            ) : (
              <div className="divide-y divide-slate-700/60">
                {documents.map((d) => (
                  <div key={d.filename} className="py-4 flex items-center justify-between gap-4">
                    <div>
                      <div className="font-semibold">{d.filename}</div>
                      <div className="text-xs text-slate-400">
                        Size: {d.size_bytes} bytes • {d.modified}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => downloadDocument(d.filename)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white"
                      >
                        <Download size={16} /> Download
                      </button>
                      <button
                        onClick={() => deleteDocument(d.filename)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </main>

      <footer className="py-8 text-center text-xs text-slate-500">
        Backend: {API_URL} • Local test mode
      </footer>
    </div>
  );
}

/* UI bits */

function TabButton({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap inline-flex items-center gap-2 px-4 py-3 font-semibold transition-all border-b-2 ${
        active
          ? "text-cyan-300 border-cyan-400"
          : "text-slate-400 border-transparent hover:text-slate-200"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function Card({ children }) {
  return (
    <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 shadow-xl">
      {children}
    </div>
  );
}

function CardHeader({ title, subtitle, right }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <div className="text-xl font-bold text-white">{title}</div>
        {subtitle && <div className="text-sm text-slate-400 mt-1">{subtitle}</div>}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-left text-sm font-semibold text-cyan-300 ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "", colSpan }) {
  return (
    <td colSpan={colSpan} className={`px-4 py-3 text-slate-200 ${className}`}>
      {children}
    </td>
  );
}

function Notice({ type = "info", children }) {
  return (
    <div
      className={`mb-3 rounded-xl border px-4 py-3 ${
        type === "error"
          ? "border-red-500/40 bg-red-500/10 text-red-200"
          : "border-slate-700 bg-slate-800/40 text-slate-200"
      }`}
    >
      {children}
    </div>
  );
}
